import { type ReactNode } from 'react';
import { cn } from './cn';
import { Spinner } from './Spinner';

export interface TableColumn<T> {
  /** Stable key; also used as React key for the header cell. */
  key: string;
  header: ReactNode;
  /** Cell renderer for a row. */
  render: (row: T) => ReactNode;
  className?: string;
  /** Header/cell text alignment. */
  align?: 'left' | 'right' | 'center';
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  rows: T[];
  /** Stable row key extractor. */
  rowKey: (row: T) => string;
  loading?: boolean;
  /** Rendered when there are no rows and not loading. */
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  className?: string;
}

const ALIGN: Record<NonNullable<TableColumn<unknown>['align']>, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

/** Generic, typed data table with loading and empty states. */
export function Table<T>({
  columns,
  rows,
  rowKey,
  loading,
  empty,
  onRowClick,
  className,
}: TableProps<T>) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl border border-surface-border',
        className,
      )}
    >
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-surface-1">
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={cn(
                  'px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500',
                  ALIGN[col.align ?? 'left'],
                  col.className,
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-10 text-center text-zinc-500"
              >
                <Spinner label="Loading rows" />
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-10 text-center text-sm text-zinc-500"
              >
                {empty ?? 'No records.'}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
                role={onRowClick ? 'button' : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                className={cn(
                  'border-t border-surface-border transition-colors',
                  onRowClick &&
                    'cursor-pointer hover:bg-surface-3 focus:outline-none focus-visible:bg-surface-3',
                )}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={cn(
                      'px-4 py-3 text-zinc-200',
                      ALIGN[col.align ?? 'left'],
                      col.className,
                    )}
                  >
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
